import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faBullhorn,
  faCalendarAlt,
  faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";

import { CallForOpportunity } from "../../../api/models/call-for-opportunity";
import { ProfileLookingFor } from "../../../api/models/profile-looking-for";
import { ProfileLookingForUtils } from "../../../shared/utils/student-profile.utils";
import { LocaleService } from "../../../shared/services/locale.service";

/**
 * Compact, identity-free card for the student's own announcements
 * ("Mijn oproepen") tab. Because every card belongs to the logged-in student,
 * the name, avatar and link-to-student-profile that the shared
 * <app-call-for-opportunity-card>/NetworkCard render are pure noise here.
 *
 * This card leads with what actually differs between a student's own
 * announcements — the opportunity type, the date range, the education and the
 * personal description — and nothing else.
 *
 * Browse/company contexts deliberately keep using the richer NetworkCard.
 *
 * @see User story #18091
 */
@Component({
  selector: "app-own-announcement-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, FontAwesomeModule],
  host: { class: "flex grow w-full min-w-0" },
  template: `
    <div
      data-testid="own-announcement-card"
      class="flex flex-col grow w-full rounded-md border border-mty-body/10 bg-white p-4 transition-shadow hover:shadow-md"
    >
      <!-- Opportunity type -->
      <span
        data-testid="type-chip"
        class="inline-flex items-center self-start gap-1.5 rounded-full bg-mty-accent-light px-2.5 py-1 text-xs font-medium text-mty-accent"
      >
        <fa-icon [icon]="faBullhorn" class="text-[10px]"></fa-icon>
        {{ lookingForKey() | translate }}
      </span>

      <!-- Date range -->
      @if (startLabel()) {
        <p
          data-testid="date-range"
          class="mt-3 flex items-center gap-2 text-sm text-mty-body/70"
        >
          <fa-icon [icon]="faCalendarAlt" class="text-mty-primary text-xs"></fa-icon>
          <span>
            {{ startLabel() }} – {{ endLabel() ?? ("Ongoing" | translate) }}
          </span>
        </p>
      }

      <!-- Education -->
      @if (education()) {
        <p
          data-testid="education"
          class="mt-1 flex items-center gap-2 text-sm text-mty-body/70"
        >
          <fa-icon [icon]="faGraduationCap" class="text-mty-primary text-xs"></fa-icon>
          <span class="truncate">{{ education() }}</span>
        </p>
      }

      <!-- Personal description -->
      @if (description()) {
        <p
          data-testid="description"
          class="mt-3 border-t border-mty-body/10 pt-3 text-sm text-mty-body/80 line-clamp-2"
        >
          {{ description() }}
        </p>
      }
    </div>
  `,
})
export class OwnAnnouncementCardComponent {
  private readonly localeService = inject(LocaleService);

  protected readonly faBullhorn = faBullhorn;
  protected readonly faCalendarAlt = faCalendarAlt;
  protected readonly faGraduationCap = faGraduationCap;

  readonly data = input.required<CallForOpportunity>();

  /** Translation key for the opportunity type badge (e.g. "Internship"). */
  protected readonly lookingForKey = computed(() =>
    ProfileLookingForUtils.getTranslationKey(
      this.data().lookingFor as ProfileLookingFor,
    ),
  );

  protected readonly startLabel = computed(() =>
    this.formatDate(this.data().startDateTime),
  );

  /** `null` when the announcement has no end date — template shows "Ongoing". */
  protected readonly endLabel = computed(() => {
    const end = this.data().endDateTime;
    return this.isOpenEnded(end) ? null : this.formatDate(end);
  });

  protected readonly education = computed(
    () => this.data().highestEducation || undefined,
  );

  protected readonly description = computed(
    () => this.data().personalDescription || undefined,
  );

  /**
   * Treats null/empty and .NET `DateTime.MinValue` (serialized as
   * "0001-01-01T00:00:00") as "no end date" — both occur in the API payload.
   */
  private isOpenEnded(dateString: string | null | undefined): boolean {
    return !dateString || dateString.startsWith("0001-01-01");
  }

  private formatDate(dateString: string | null | undefined): string | undefined {
    if (!dateString) return undefined;
    try {
      return new Date(dateString).toLocaleDateString(
        this.localeService.getCurrentLanguage(),
      );
    } catch {
      return dateString;
    }
  }
}
